import React, {
    useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';

/**
 * 사양 제목별 서버에 등록된 옵션으로 드롭다운 표시.
 * DB에 없는 기존 값은 "(기존값)" 옵션으로 유지해 선택 상태가 깨지지 않게 함.
 * 패널 열면 옵션 목록을 검색어로 필터링할 수 있음.
 */
const SpecDropdownSelect = ({
    specTitle,
    value,
    onChange,
    options = [],
    disabled = false,
    className = '',
}) => {
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [panelRect, setPanelRect] = useState(null);

    const sorted = useMemo(() => {
        const forTitle = options.filter((o) => o.specTitle === specTitle);
        return [...forTitle].sort((a, b) => (
            (a.sortOrder || 0) - (b.sortOrder || 0)
            || String(a.optionValue || '').localeCompare(String(b.optionValue || ''), 'ko')
        ));
    }, [options, specTitle]);

    const optionValues = useMemo(() => new Set(sorted.map((o) => o.optionValue)), [sorted]);
    const raw = value == null ? '' : String(value);
    const hasLegacy = raw !== '' && !optionValues.has(raw);

    const q = searchQuery.trim().toLowerCase();
    const filteredSorted = useMemo(() => {
        if (!q) {
            return sorted;
        }
        return sorted.filter((o) => String(o.optionValue || '').toLowerCase().includes(q));
    }, [sorted, q]);

    const showLegacyRow = hasLegacy && (
        !q
        || raw.toLowerCase().includes(q)
        || '(기존값)'.includes(q)
        || `기존값 ${raw}`.toLowerCase().includes(q)
    );

    const updatePanelRect = () => {
        const el = triggerRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        setPanelRect({
            top: r.bottom + 4,
            left: r.left,
            width: Math.max(r.width, 160),
        });
    };

    useLayoutEffect(() => {
        if (!open) {
            setPanelRect(null);
            return undefined;
        }
        updatePanelRect();
        window.addEventListener('resize', updatePanelRect);
        window.addEventListener('scroll', updatePanelRect, true);
        return () => {
            window.removeEventListener('resize', updatePanelRect);
            window.removeEventListener('scroll', updatePanelRect, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onDocMouseDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);

    const displayLabel = raw === '' ? '선택' : raw;

    const pick = (next) => {
        onChange(next);
        setOpen(false);
        setSearchQuery('');
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }
        setOpen((prev) => {
            const next = !prev;
            if (next) {
                setSearchQuery('');
            }
            return next;
        });
    };

    const triggerClasses = ['spec-dropdown-trigger', className].filter(Boolean).join(' ');
    const empty = filteredSorted.length === 0 && !showLegacyRow && q !== '';

    const panelStyle = panelRect
        ? {
            position: 'fixed',
            top: panelRect.top,
            left: panelRect.left,
            width: panelRect.width,
            zIndex: 5500,
        }
        : { display: 'none' };

    return (
        <div className="spec-dropdown-root" ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClasses}
                onClick={toggleOpen}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <span className="spec-dropdown-trigger-label">{displayLabel}</span>
                <span className="spec-dropdown-trigger-chevron" aria-hidden>▾</span>
            </button>
            {open && (
                <div className="spec-dropdown-panel" style={panelStyle} role="listbox">
                    <input
                        type="text"
                        className="spec-dropdown-search field-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="옵션 검색…"
                        autoComplete="off"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                    <ul className="spec-dropdown-list">
                        <li>
                            <button
                                type="button"
                                className="spec-dropdown-option"
                                onClick={() => pick('')}
                            >
                                선택
                            </button>
                        </li>
                        {showLegacyRow && (
                            <li>
                                <button
                                    type="button"
                                    className="spec-dropdown-option spec-dropdown-option-legacy"
                                    onClick={() => pick(raw)}
                                >
                                    {`(기존값) ${raw}`}
                                </button>
                            </li>
                        )}
                        {filteredSorted.map((o) => (
                            <li key={o.id}>
                                <button
                                    type="button"
                                    className={`spec-dropdown-option${raw === o.optionValue ? ' is-selected' : ''}`}
                                    onClick={() => pick(o.optionValue)}
                                >
                                    {o.optionValue}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {empty && (
                        <div className="spec-dropdown-empty">검색 결과가 없습니다.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SpecDropdownSelect;
